import { memo, useCallback, useMemo, useState } from 'react';
import { Input, InputProps } from '../../designSystem/Input';
import { PageRangeParser } from '@page/core/services/fileSources/parser';

export interface PageNumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'error' | 'onError'> {
  totalPages: number;
  initialValue?: string;
  error?: string | null;
  onChange: (value: string, pageIndices: number[] | null, error: string | null) => void;
  onError?: (error: string) => void;
}

const PageNumberInputBase = ({
  totalPages,
  initialValue,
  error: errorFromProps,
  label = 'Page ranges',
  placeholder = 'e.g. 1-5, 8, 11-13',
  onChange,
  onError,
  ...rest
}: PageNumberInputProps) => {
  const [error, setError] = useState<string | null | undefined>(errorFromProps);
  const [value, setValue] = useState<string | undefined>(initialValue);

  const parser = useMemo(() => new PageRangeParser(totalPages), [totalPages]);

  const handleChange = useCallback(
    (inputValue: string) => {
      if (!parser.allowCharacters(inputValue)) return;

      let validationError: string | null = null;
      let pageIndices: number[] | null = null;

      if (inputValue) {
        const result = parser.toPageIndices(inputValue);

        if (result.error) {
          validationError = result.error;
          pageIndices = null;
        } else {
          validationError = null;
          pageIndices = result.data;
        }
      }

      if (validationError) {
        onError?.(validationError);
      }

      setError(validationError);
      setValue(inputValue);
      onChange(inputValue, pageIndices, validationError);
    },
    [onChange, parser, onError],
  );

  return (
    <Input
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(event) => handleChange(event.target.value)}
      error={errorFromProps || error}
      type="text"
      {...rest}
    />
  );
};

export const PageNumberInput = memo(PageNumberInputBase);
